import React, { useEffect, useState } from 'react';
import { AUIModal } from '@ancert/aui-modal';
import { format } from 'date-fns';
import { bool, func, objectOf, shape } from 'prop-types';
import { useForm } from 'react-hook-form';
import { useNotification } from '@ancert/aui-notification-context';
import AUIInputTextField from '@ancert/aui-input-text-field';
import AUICalendarField from '@ancert/aui-calendar-field';
import AUIInputSwitchField from '@ancert/aui-input-switch-field';
import AUIDropdownField from '@ancert/aui-dropdown-field';
import AUIMessage from '@ancert/aui-message';
import { defaultValues, resolver } from './form';
import { ROL_OPTIONS } from '../../../../constants/user.constants';
import { ROL } from '../../../../lib/role.enum';
import { FORMAT_DATE } from '../../../../constants';
import { postNewUser, putEditUser } from '../../../../services/user.service';
import AsignarDenuncia from './AsignarDenuncia';
import DenunciaSeleccionada from './DenunciaSeleccionada';
import { deleteUnssignComplaint, putAsignarUsuario } from '../../../../services/denuncias.service';
import MAESTRO_TIPO_DOC from '../../../../constants/maestros.constants';

const ModalUsuario = ({ show, onClose, reload, editData, complaintsAssigned }) => {
  const { openNotification } = useNotification();
  const [complaintSelected, setComplaintSelected] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const { control, formState, handleSubmit, reset, watch } = useForm({
    defaultValues,
    resolver,
    mode: 'all',
    reValidateMode: 'onChange'
  });

  const allInputFields = watch();

  const checkChanges = () => {
    const changesDetectedForm = Object.keys(allInputFields).some((key) => {
      if (allInputFields[key] instanceof Date && editData[key] instanceof Date) {
        return allInputFields[key].getTime() !== editData[key].getTime();
      }
      return allInputFields[key] !== editData[key];
    });
    const changesDetectedComplaintsAssigned = Object.keys(complaintsAssigned).some(
      (key) => complaintSelected[key]?.complaint.id !== complaintsAssigned[key]?.complaint.id
    );

    const changesDetectedComplaintsSelected = Object.keys(complaintSelected).some(
      (key) => complaintSelected[key]?.complaint.id !== complaintsAssigned[key]?.complaint.id
    );

    setIsDisabled(changesDetectedForm || changesDetectedComplaintsAssigned || changesDetectedComplaintsSelected);
  };

  useEffect(() => {
    if (editData) {
      reset(editData);
      setComplaintSelected(complaintsAssigned);
    } else {
      reset(defaultValues);
    }
  }, [editData]);

  useEffect(() => {
    if (editData) checkChanges();
  }, [allInputFields]);

  const closeModal = () => {
    reset(defaultValues);
    setComplaintSelected([]);
    setErrorMessage(null);
    onClose();
  };

  const onSubmit = async (dataForm) => {
    const data = { ...dataForm };
    data.creationDate = format(new Date(dataForm.creationDate), FORMAT_DATE.BACK_DATE_HOURS_MINUTES);

    if (editData) {
      const response = await putEditUser({ data });
      if (response?.status === 200) {
        complaintSelected.forEach(async (denuncia) => {
          await putAsignarUsuario({ id: denuncia.complaint.id, idAuthority: editData.id });
        });

        const diffComplaints = complaintsAssigned.filter(
          (complaintAssigned) =>
            !complaintSelected.some(
              (complaintSelect) => complaintAssigned.complaint.id === complaintSelect.complaint.id
            )
        );

        if (diffComplaints.length > 0) {
          diffComplaints.forEach(async (differingObject) => {
            await deleteUnssignComplaint({ id: differingObject.complaint.id, idAuthority: editData.id });
          });
        }

        openNotification({
          title: 'Editar usuario',
          content: 'Usuario editado correctamente',
          type: 'success',
          centerButtons: true,
        });
        closeModal();
        reload();
      } else {
        setErrorMessage('Se ha producido un error al editar al usuario.');
      }
    } else {
      const response = await postNewUser({ data });
      if (response?.status === 200) {
        openNotification({
          title: 'Añadir usuario',
          content: 'Usuario añadido correctamente',
          type: 'success',
          centerButtons: true,
        });
        closeModal();
        reload();
      } else {
        setErrorMessage('Se ha producido un error al añadir el nuevo usuario.');
      }
    }
  };

  return (
    <AUIModal
      className="modal-nuevo-usuario"
      size={editData ? 'xxl' : 'xl'}
      title={editData ? 'Usuario' : 'Nuevo usuario'}
      show={show}
      onClose={closeModal}
      autoHeight
      actionButtons={[
        {
          id: 'primary',
          mode: 'primary',
          label: 'Guardar',
          disabled: editData ? !isDisabled : false,
          onClick: handleSubmit(onSubmit),
        },
        {
          id: 'tertiary',
          mode: 'tertiary',
          label: 'Cancelar',
          onClick: closeModal,
        },
      ]}
    >
      <form autoComplete="off">
        <div className="o-grid">
          <div className="o-grid__row">
            <div className="o-grid__col-4">
              <AUIInputTextField
                required
                label="Nombre"
                id="name"
                name="name"
                controlRef={control}
                errors={formState.errors}
              />
            </div>
            <div className="o-grid__col-4">
              <AUIInputTextField
                required
                label="Primer apellido"
                name="lastname1"
                controlRef={control}
                errors={formState.errors}
              />
            </div>
            <div className="o-grid__col-4">
              <AUIInputTextField
                label="Segundo apellido"
                name="lastname2"
                controlRef={control}
                errors={formState.errors}
              />
            </div>
          </div>
          <div className="o-grid__row">
            <div className="o-grid__col-4">
              <AUIDropdownField
                required
                disabled={editData}
                placeholder="Seleccione una opción"
                name="role"
                label="Tipo de usuario"
                controlRef={control}
                errors={formState.errors}
                options={ROL_OPTIONS.filter((rol) => [ROL.AUTORIDAD, ROL.RESPONSABLE].includes(rol.value))}
                appendTo={null}
              />
            </div>
            <div className="o-grid__col-4">
              <AUIDropdownField
                appendTo={null}
                required
                label="Tipo de documento"
                name="documentType"
                placeholder="Seleccionar"
                controlRef={control}
                errors={formState.errors}
                options={MAESTRO_TIPO_DOC}
              />
            </div>
            <div className="o-grid__col-4">
              <AUIInputTextField
                required
                label="Documento de identidad"
                name="document"
                controlRef={control}
                errors={formState.errors}
              />
            </div>
          </div>
          <div className="o-grid__row">
            <div className="o-grid__col-4">
              <AUIInputTextField
                required
                label="Correo electrónico"
                name="email"
                controlRef={control}
                errors={formState.errors}
              />
            </div>
            <div className="o-grid__col-4">
              <AUICalendarField
                required
                name="creationDate"
                label="Fecha"
                showNavigation
                appendTo={null}
                maxDate={new Date()}
                controlRef={control}
                errors={formState.errors}
                placeholder="Seleccionar"
              />
            </div>
            <div className="o-grid__col-4">
              <AUIInputSwitchField
                required
                label="Estado"
                name="active"
                controlRef={control}
                description="Activo"
                errors={formState.errors}
              />
            </div>
          </div>
        </div>
        {errorMessage && (
          <AUIMessage
            className="error-message"
            closable={false}
            content={errorMessage}
            status="error"
            onClose={() => setErrorMessage(null)}
          />
        )}{' '}
        {editData && (
          <div>
            <AsignarDenuncia setComplaintSelected={setComplaintSelected} complaintSelected={complaintSelected} />
            <DenunciaSeleccionada complaintSelected={complaintSelected} setComplaintSelected={setComplaintSelected} />
          </div>
        )}
      </form>
    </AUIModal>
  );
};
ModalUsuario.propTypes = {
  onClose: func.isRequired,
  reload: func.isRequired,
  show: bool,
  editData: objectOf(shape({})),
  complaintsAssigned: objectOf(shape({})),
};
ModalUsuario.defaultProps = {
  show: false,
  editData: null,
  complaintsAssigned: null,
};
export default ModalUsuario;
